import {Injectable} from '@angular/core';
import {LinearFilter, Mesh, MeshBasicMaterial, TextureLoader, Vector3} from 'three';
import {RenderService} from '../Scene/render.service';
import {ContainerService} from '../../Utils/container.service';
import {OrbitControlsService} from '../Control/orbit-controls.service';
import {SceneService} from '../Scene/scene.service';
import {EditorControlService} from '../../API/Public/editor-control.service';

@Injectable({
    providedIn: 'root'
})
export class PictureService {

    public readonly dragControl = 'dragImage';

    public zoom: number;
    public opacity: number;
    public brightness: number;
    public contrast: number;
    public canvas: HTMLCanvasElement;
    public pictureCanvas: HTMLCanvasElement;
    public pictureDrawn = false;
    private material: MeshBasicMaterial;

    private _initialScale: Vector3;

    constructor(private renderService: RenderService,
                private containerService: ContainerService,
                private controlService: OrbitControlsService,
                private editorControlService: EditorControlService) {
        this.zoom = 0.5;
        this.opacity = 1;
        this.brightness = 100;
        this.contrast = 100;

        this.registerControl();
    }

    get initialScale(): Vector3 {
        return this._initialScale;
    }

    /**
     * delete image from render scene and resets all properties
     */
    public clear() {
        this.pictureDrawn = false;
        this.zoom = 0.5;
        this.opacity = 1;
        this.brightness = 100;
        this.contrast = 100;
        if (this.material) {
            this.material.map = null;
            this.material.opacity = 0;
            this.material.needsUpdate = true;
        }
        this.controlService.dragControls.deactivate();
        this.controlService.orbitControls.enabled = true;
        this.renderService.render();
    }

    /**
     * change opacity of image plane
     * @param value
     */
    public changeOpacity(value: number) {
        this.opacity = value;
        this.material.opacity = value;
        this.material.needsUpdate = true;
        this.renderService.render();
    }

    /**
     * change zoom factor of image plane
     * @param value
     */
    public changeZoom(value: number) {
        this.zoom = value;
        this.containerService.imagePlane.scale.set(value, value, 1);
        // this.material.map.repeat.set(value, value);
        // this.material.needsUpdate = true;
        this.renderService.render();
    }

    /**
     * loads image and add to render scene
     * @param img
     */
    public setImage(img: any) {
        const mesh = (this.containerService.imagePlane.children[0] as Mesh);
        const loader = new TextureLoader();
        loader.load(img, (texture) => {
            const ratio = texture.image.width / texture.image.height;
            // ratio = (ratio - this.zoom);
            this.containerService.imagePlane.children[0].scale.set(this.zoom * ratio, this.zoom, 1);
            this.material = new MeshBasicMaterial({
                map: texture,
                opacity: this.opacity
            });
            texture.minFilter = LinearFilter;
            this.material.transparent = true;
            mesh.material = this.material;
            this.renderService.render();
            this._initialScale = mesh.scale.clone();
            this.pictureDrawn = true;
        });
    }

    /**
     * resets all image properties
     */
    public resetImg() {
        this.zoom = 0.5;
        this.opacity = 1;
        this.controlService.activateDrag(false);
        this.controlService.orbitControls.enabled = true;
        // reset image size
        this.containerService.imagePlane.scale.set(1, 1, 1);
        // reset image opacity
        this.material.opacity = this.opacity;
        // reset image position
        this.containerService.imagePlane.children[0].position.set(0, 0, 0);
        // render image plane to scene
        this.material.needsUpdate = true;

        this.renderService.render();

    }

    /**
     * enable or disable image movement
     */
    public dragImage() {
        const pictureStatic = this.controlService.dragEnabled;
        if (!pictureStatic) {
            this.controlService.activateDrag(true);
            this.controlService.orbitControls.enabled = false;
        } else {
            this.controlService.activateDrag(false);
            this.controlService.orbitControls.enabled = true;
        }

    }

    /**
     * if picture is movable returns true; else false
     */
    public getMoveStatus() {
        return this.controlService.dragEnabled;
    }

    /**
     * rotate overlay image
     * @param direction
     * @param angle
     */
    public rotateOverlay(direction, angle) {
        if (direction === 'left') {
            this.containerService.imagePlane.children[0].rotateZ(angle);
        } else if (direction === 'right') {
            this.containerService.imagePlane.children[0].rotateZ(angle);
        }
        this.renderService.render();
    }

    // -- event controls -------------------------------

    private registerControl() {
        this.editorControlService.registerControl(this.dragControl, {
            events: new Map(),

            init: this.dragImage.bind(this),
            finish: this.dragImage.bind(this),

            pan: false,
            rotate: false,
            zoom: true
        });
    }

}
